export interface NodeProperty {
    [key: string]: string | number | boolean | null | undefined;
}

export interface Node {
    component_type: string;
    "Primary ID": string;
    Aliases: string[];
    component_order: number | null;
    properties?: NodeProperty;
    configuration?: NodeProperty;
    pref?: any;
    api_child_count?: number;
}

export interface Edge {
    source: string;
    target: string;
    relation: string;
    signal: string;
    properties?: NodeProperty;
    configuration?: NodeProperty;
    pref?: any;
}

export interface ProjectMetadata {
    project_name: string | null;
    project_address: string | null;
    project_lat_lon: string | null;
    project_capacity_mw: string | null;
}

export interface PVSiteData {
    nodes: Node[];
    edges: Edge[];
    project_metdata: ProjectMetadata;
}

export interface GraphNode {
    id: string;
    label: string;
    type: string;
    color: string;
    group?: string;
    details: Node;
    val?: number; // For d3/force-graph weight
    tier: number; // 0 = Grid, 8 = Module
    // Topology for Grid Layout
    siblingIndex: number; // Index among siblings
    groupIndex: number; // Index among same-type group
    parentId?: string;
    cumulativePower?: number; // Total Watts
    normalizedPower?: number; // 0-1 value relative to tier
    isAncillary?: boolean; // Flag for DAS/Comms/Ancillary nodes
    apiChildCount?: number; // Child count from API for lazy expansion
}

export interface GraphLink {
    source: string;
    target: string;
    label: string;
    color: string;
    details: Edge;
}

// Color mapping for component types
// Organized by functional category with consistent color families
export const ComponentColors: Record<string, string> = {
    // === GRID & UTILITY (White/Gray family) ===
    grid_connection_point: '#FFFFFF', // White
    grid: '#FFFFFF',                  // White
    revenue_meter: '#9CA3AF',         // Gray

    // === MEDIUM VOLTAGE (Dark Gray/Slate family) ===
    switchgear_mv: '#64748B',         // Slate Gray
    protective_relay: '#475569',      // Slate
    surge_protection_device: '#94A3B8', // Light Slate

    // === TRANSFORMERS (Blue family) ===
    transformer: '#3B82F6',           // Blue
    voltage_transformer: '#60A5FA',   // Light Blue
    current_transformer: '#2563EB',   // Darker Blue
    control_transformer: '#93C5FD',   // Sky Blue

    // === LOW VOLTAGE DISTRIBUTION (Rose/Magenta family) ===
    switchboard_lv: '#BE185D',        // Dark Rose
    panelboard: '#DB2777',            // Rose
    disconnect_switch: '#EC4899',     // Pink

    // === PROTECTION & SWITCHING (Red family) ===
    circuit_breaker: '#DC2626',       // Red
    fuse: '#EF4444',                  // Light Red
    rapid_shutdown_device: '#F87171', // Rose Red
    grounding_system: '#78716C',      // Stone

    // === POWER CONVERSION (Orange/Amber family) ===
    inverter: '#F59E0B',              // Orange
    pcs_bidirectional: '#D97706',     // Dark Amber
    pcs_skid: '#B45309',              // Brown-Orange (container)
    pcs_tap: '#EA580C',               // Bright Orange
    microinverter: '#FCD34D',         // Yellow

    // === CABLING (Copper/Bronze family) ===
    harness: '#CD7F32',               // Bronze

    // === DC COLLECTION (Amber/Yellow family) ===
    mppt: '#FBBF24',                  // Amber
    dc_optimizer: '#FDE047',          // Yellow
    combiner_box: '#F97316',          // Orange

    // === STRING LEVEL (Green family) ===
    string: '#34D399',                // Light Green
    junction_box: '#4ADE80',          // Emerald

    // === MODULE LEVEL (Deep Green family) ===
    pv_module: '#10B981',             // Green
    racking_system: '#047857',        // Dark Emerald
    tracker_controller: '#059669',    // Teal Green

    // === COMMUNICATIONS & MONITORING (Blue family - hierarchical) ===
    // Tier 1: Site-level supervisory control
    scada: '#1E3A5F',                 // Navy Blue
    control_system: '#1E3A5F',        // Navy Blue
    hmi: '#234876',                   // Dark Blue

    // Tier 2: Site-level data aggregation
    gateway: '#075985',               // Dark Sky Blue
    rtu: '#0369A1',                   // Deep Sky Blue
    plc: '#0284C7',                   // Sky Blue

    // Tier 3: Intermediate data collection
    data_logger: '#0891B2',           // Dark Cyan
    das: '#0891B2',                   // Dark Cyan
    communication_hub: '#06B6D4',     // Cyan

    // Tier 4: Monitoring stations
    weather_station: '#0EA5E9',       // Light Sky Blue
    meter: '#22D3EE',                 // Light Cyan
    power_meter: '#22D3EE',           // Light Cyan

    // Tier 5: Field-level aggregation
    sensor_hub: '#38BDF8',            // Lighter Sky Blue
    repeater: '#38BDF8',              // Lighter Sky Blue

    // Tier 6: Environmental sensors
    pyranometer: '#67E8F9',           // Pale Cyan
    anemometer: '#7DD3FC',            // Pale Sky Blue
    rain_gauge: '#A5F3FC',            // Very Light Cyan

    // Tier 7: Ambient sensors
    ambient_temp_sensor: '#BAE6FD',   // Very Pale Sky Blue
    humidity_sensor: '#BAE6FD',       // Very Pale Sky Blue
    barometer: '#BAE6FD',             // Very Pale Sky Blue

    // Tier 8: Equipment-level sensors
    module_temp_sensor: '#CFFAFE',    // Near White Cyan
    current_sensor: '#CFFAFE',        // Near White Cyan
    voltage_sensor: '#CFFAFE',        // Near White Cyan

    // Tier 9: Generic sensors
    sensor: '#E0F2FE',                // Lightest Sky Blue

    // === DEFAULT ===
    default: '#9CA3AF'                // Gray
};
